import { api } from './api.js';
import { esc, fmt$, CATEGORY_COLORS } from './utils.js';

const TOTAL_STEPS = 5;

let currentStep = 1;
let stepData = {}; // accumulated form values across steps
let categories = []; // loaded once after step 1 creates them

// Called by app.js with the step to resume at
export async function startOnboarding(resumeStep = 1) {
  currentStep = Math.max(1, resumeStep);
  document.getElementById('onboarding-screen').classList.remove('hidden');
  document.getElementById('auth-screen').classList.add('hidden');
  document.getElementById('app-shell').classList.add('hidden');
  renderStep(currentStep);
}

// ── PROGRESS DOTS ─────────────────────────────────────────────────────────

function renderDots(active) {
  const el = document.getElementById('onboarding-progress');
  el.innerHTML = Array.from({ length: TOTAL_STEPS }, (_, i) => {
    const n = i + 1;
    const cls = n < active ? 'done' : n === active ? 'active' : '';
    return `<div class="progress-dot ${cls}"></div>`;
  }).join('');
}

// ── STEP ROUTER ───────────────────────────────────────────────────────────

function renderStep(step) {
  renderDots(step);
  const el = document.getElementById('onboarding-step');
  if      (step === 1) renderStep1(el);
  else if (step === 2) renderStep2(el);
  else if (step === 3) renderStep3(el);
  else if (step === 4) renderStep4(el);
  else if (step === 5) renderStep5(el);
}

function backBtn() {
  return `<div class="step-back" id="back-btn">← Back</div>`;
}

function wireBack(step) {
  document.getElementById('back-btn')?.addEventListener('click', () => {
    currentStep = step - 1;
    renderStep(currentStep);
  });
}

// ── STEP 1 — How Are You Paid? ────────────────────────────────────────────

function renderStep1(el) {
  const d = stepData.step1 ?? {};
  const type = d.incomeType ?? 'hourly_commission';

  el.innerHTML = `
    <div class="step-title">How are you paid?</div>
    <div class="step-subtitle">We use this to calculate your paychecks and surplus.</div>

    <div class="income-type-grid">
      ${[
        { id: 'hourly_commission', label: 'Hourly + commission', desc: 'Variable hours plus commission income' },
        { id: 'hourly',           label: 'Hourly only',         desc: 'Fixed hours, regular paycheck' },
        { id: 'commission',       label: 'Commission only',     desc: 'Income varies month to month' },
        { id: 'salary',           label: 'Salary',              desc: 'Fixed amount each paycheck' },
      ].map(o => `
        <div class="income-type-card${type === o.id ? ' selected' : ''}" data-type="${o.id}">
          <div class="selected-dot"></div>
          <div class="card-label">${o.label}</div>
          <div class="card-desc">${o.desc}</div>
        </div>`).join('')}
    </div>

    <!-- Hourly rate fields (shown for hourly/hourly_commission) -->
    <div id="hourly-fields" style="display:${type === 'salary' || type === 'commission' ? 'none' : 'block'}">
      <div class="form-row">
        <div class="form-group">
          <label class="form-label">Gross hourly rate</label>
          <input type="number" id="gross-rate" placeholder="0.00" step="0.01" value="${esc(d.grossHourlyRate ?? '')}">
        </div>
        <div class="form-group">
          <label class="form-label">Net take-home rate</label>
          <input type="number" id="net-rate" placeholder="0.00" step="0.01" value="${esc(d.netHourlyRate ?? '')}">
        </div>
      </div>
      <div class="field-helper">Net is what actually lands in your bank account after taxes.</div>
    </div>

    <!-- Salary field -->
    <div id="salary-fields" style="display:${type === 'salary' ? 'block' : 'none'}" class="form-group">
      <label class="form-label">Net amount per paycheck</label>
      <input type="number" id="salary-paycheck" placeholder="0.00" step="0.01" value="${esc(d.salaryPerPaycheck ?? '')}">
    </div>

    <div class="form-row" style="margin-top:var(--space-4)">
      <div class="form-group">
        <label class="form-label">Pay period starts on</label>
        <select id="pay-day">
          ${['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'].map((day, i) =>
            `<option value="${i}" ${(d.payStartDay ?? 6) === i ? 'selected' : ''}>${day}</option>`
          ).join('')}
        </select>
      </div>
      <div class="form-group">
        <label class="form-label">Pay period length</label>
        <select id="pay-length">
          <option value="7"  ${(d.payPeriodLengthDays ?? 14) === 7  ? 'selected' : ''}>Weekly</option>
          <option value="14" ${(d.payPeriodLengthDays ?? 14) === 14 ? 'selected' : ''}>Bi-weekly (14 days)</option>
          <option value="30" ${(d.payPeriodLengthDays ?? 14) === 30 ? 'selected' : ''}>Monthly</option>
        </select>
      </div>
    </div>
    <div class="form-group" style="margin-top:var(--space-2)">
      <label class="form-label">When did your <em>current</em> pay period start? <span style="color:var(--text-tertiary);font-weight:400">(anchor date)</span></label>
      <input type="date" id="pay-anchor" value="${d.payPeriodAnchor ?? getRecentWeekday(d.payStartDay ?? 6)}">
      <div class="field-helper">We pre-fill the most recent occurrence of your selected day, but you can correct it if your cycle started earlier.</div>
    </div>

    <div class="form-group" style="margin-top:var(--space-5)">
      <label class="form-label">Birthday <span style="color:var(--text-tertiary);font-weight:400">(optional)</span></label>
      <div class="birthday-row">
        <input type="number" id="bday-month" placeholder="MM" min="1" max="12" value="${esc(d.birthMonth ?? '')}">
        <input type="number" id="bday-day"   placeholder="DD" min="1" max="31" value="${esc(d.birthDay ?? '')}">
        <input type="number" id="bday-year"  placeholder="YYYY" min="1950" max="2010" value="${esc(d.birthYear ?? '')}">
      </div>
      <div class="field-helper">Used for Roth IRA projections and a little surprise 🎂</div>
    </div>

    <div id="step1-err" class="auth-error"></div>
    <button class="btn btn-primary w-full" id="step1-continue" style="justify-content:center;padding:14px;margin-top:var(--space-4)">Continue →</button>
    <div style="text-align:center;margin-top:var(--space-3)">
      <button class="btn btn-ghost" id="step1-skip" style="font-size:.8rem;color:var(--text-tertiary)">Skip for now</button>
    </div>`;

  // Income type card selection
  el.querySelectorAll('.income-type-card').forEach(card => {
    card.addEventListener('click', () => {
      el.querySelectorAll('.income-type-card').forEach(c => c.classList.remove('selected'));
      card.classList.add('selected');
      const t = card.dataset.type;
      el.querySelector('#hourly-fields').style.display = (t === 'hourly' || t === 'hourly_commission') ? 'block' : 'none';
      el.querySelector('#salary-fields').style.display = t === 'salary' ? 'block' : 'none';
    });
  });

  // Auto-update anchor date when weekday dropdown changes
  el.querySelector('#pay-day')?.addEventListener('change', (e) => {
    const anchorInput = el.querySelector('#pay-anchor');
    if (anchorInput) anchorInput.value = getRecentWeekday(parseInt(e.target.value));
  });

  const continueHandler = async (skip = false) => {
    const errEl = el.querySelector('#step1-err');
    errEl.classList.remove('show');

    const type    = el.querySelector('.income-type-card.selected')?.dataset.type ?? 'hourly_commission';
    const dayIdx  = parseInt(el.querySelector('#pay-day').value);
    const length  = parseInt(el.querySelector('#pay-length').value);
    // Use explicit anchor date from picker; fallback to computed most-recent weekday
    const anchor  = el.querySelector('#pay-anchor')?.value || getRecentWeekday(dayIdx);

    const gross = parseFloat(el.querySelector('#gross-rate')?.value) || undefined;
    const net   = parseFloat(el.querySelector('#net-rate')?.value)   || undefined;
    const salary= parseFloat(el.querySelector('#salary-paycheck')?.value) || undefined;
    const month = parseInt(el.querySelector('#bday-month').value) || undefined;
    const day   = parseInt(el.querySelector('#bday-day').value)   || undefined;
    const year  = parseInt(el.querySelector('#bday-year').value)  || undefined;

    if (!skip && (type === 'hourly' || type === 'hourly_commission') && (!gross || !net)) {
      errEl.textContent = 'Please enter your gross and net hourly rates.';
      errEl.classList.add('show');
      return;
    }

    // Save immediately
    stepData.step1 = { incomeType: type, grossHourlyRate: gross, netHourlyRate: net,
                        salaryPerPaycheck: salary, payStartDay: dayIdx,
                        payPeriodLengthDays: length, payPeriodAnchor: anchor,
                        birthMonth: month, birthDay: day, birthYear: year };
    try {
      // Omit blank rates rather than substituting 0. The server's schema marks
      // these `.positive().optional()`, so 0 is the single value it refuses —
      // which meant "Skip for now" failed in exactly the case it exists for
      // (blank rates) and succeeded only if you had filled the fields you were
      // skipping (#65). The route itself already writes `?? 0` when a key is
      // absent, so omitting is what it was always designed to receive; this
      // just stops the client asserting a rate the user never gave.
      await api.post('/api/onboarding/step1', {
        incomeType: type,
        ...(gross  !== undefined && { grossHourlyRate: gross }),
        ...(net    !== undefined && { netHourlyRate:   net   }),
        ...(salary !== undefined && { salaryPerPaycheck: salary }),
        payPeriodAnchor: anchor,
        payPeriodLengthDays: length, birthMonth: month, birthDay: day, birthYear: year,
      });
      currentStep = 2;
      renderStep(2);
    } catch (err) {
      errEl.textContent = err.message;
      errEl.classList.add('show');
    }
  };

  el.querySelector('#step1-continue').addEventListener('click', () => continueHandler(false));
  el.querySelector('#step1-skip').addEventListener('click', () => continueHandler(true));
}

// Returns the most recent occurrence of a given weekday (0=Sun…6=Sat) as YYYY-MM-DD
function getRecentWeekday(dayIdx) {
  const today = new Date();
  const diff  = (today.getDay() - dayIdx + 7) % 7;
  const d     = new Date(today);
  d.setDate(today.getDate() - diff);
  return d.toISOString().split('T')[0];
}

// ── STEP 2 — Where Are You Starting? ─────────────────────────────────────

function renderStep2(el) {
  const d = stepData.step2 ?? {};

  const fields = [
    { id: 'cash',    label: 'Cash on hand',        section: 'assets' },
    { id: 'check',   label: 'Checking account',    section: 'assets' },
    { id: 'webull',  label: 'Brokerage (Webull)',   section: 'assets' },
    { id: 'venmo',   label: 'Venmo / CashApp',      section: 'assets' },
    { id: 'roth',    label: 'Roth IRA',             section: 'assets' },
    { id: 'physical',label: 'Physical assets',      section: 'assets', helper: 'Car, computer, equipment — rough estimate' },
    { id: 'other_a', label: 'Other assets',         section: 'assets' },
    { id: 'cc',      label: 'Credit card',          section: 'liabilities' },
    { id: 'student', label: 'Student loans',        section: 'liabilities' },
    { id: 'other_l', label: 'Other debt',           section: 'liabilities' },
  ];

  el.innerHTML = `
    ${backBtn()}
    <div class="step-title">Where are you starting?</div>
    <div class="step-subtitle">Don't worry about being exact — rough estimates work. You can update this anytime.</div>

    <div class="section-header assets">Assets</div>
    ${fields.filter(f => f.section === 'assets').map(f => `
      <div class="form-group">
        <label class="form-label">${f.label}</label>
        <input type="number" id="nw-${f.id}" placeholder="0" step="0.01" value="${esc(d[f.id] ?? '')}"
          style="font-family:var(--font-mono)">
        ${f.helper ? `<div class="field-helper">${f.helper}</div>` : ''}
      </div>`).join('')}

    <div class="section-header liabilities" style="margin-top:var(--space-2)">Liabilities</div>
    ${fields.filter(f => f.section === 'liabilities').map(f => `
      <div class="form-group">
        <label class="form-label">${f.label}</label>
        <input type="number" id="nw-${f.id}" placeholder="0" step="0.01" value="${esc(d[f.id] ?? '')}"
          style="font-family:var(--font-mono)">
      </div>`).join('')}

    <div class="nw-preview">
      <div class="nw-preview-label">Net worth</div>
      <div class="nw-preview-value" id="nw-live">$0</div>
    </div>

    <div style="font-size:.75rem;color:var(--text-tertiary);text-align:center;margin-top:var(--space-3);margin-bottom:var(--space-4)">
      This creates your first net worth snapshot. Add a new one each month to track progress.
    </div>

    <div id="step2-err" class="auth-error"></div>
    <button class="btn btn-primary w-full" id="step2-continue" style="justify-content:center;padding:14px">Continue →</button>`;

  wireBack(2);

  // Live preview
  const inputs = el.querySelectorAll('input[id^="nw-"]');
  const liveEl = el.querySelector('#nw-live');

  function updateLive() {
    const g = (id) => parseFloat(el.querySelector(`#nw-${id}`)?.value) || 0;
    const assets = g('cash') + g('check') + g('webull') + g('venmo') + g('roth') + g('physical') + g('other_a');
    const liabs  = g('cc')   + g('student') + g('other_l');
    const nw = assets - liabs;
    liveEl.textContent   = fmt$(nw);
    liveEl.style.color   = nw >= 0 ? 'var(--positive)' : 'var(--negative)';
  }
  inputs.forEach(i => i.addEventListener('input', updateLive));
  updateLive();

  el.querySelector('#step2-continue').addEventListener('click', async () => {
    const errEl = el.querySelector('#step2-err');
    errEl.classList.remove('show');
    const g = (id) => parseFloat(el.querySelector(`#nw-${id}`)?.value) || 0;

    const body = {
      cashOnHand:      g('cash'),
      checking:        g('check'),
      webull:          g('webull'),
      venmoAndCashApp: g('venmo'),
      rothIra:         g('roth'),
      physicalAssets:  g('physical'),
      otherAssets:     g('other_a'),
      creditCardDebt:  g('cc'),
      studentLoans:    g('student'),
      otherLiabilities:g('other_l'),
    };
    stepData.step2 = { cash: body.cashOnHand, check: body.checking, webull: body.webull,
                        venmo: body.venmoAndCashApp, roth: body.rothIra, physical: body.physicalAssets,
                        other_a: body.otherAssets, cc: body.creditCardDebt, student: body.studentLoans,
                        other_l: body.otherLiabilities };
    try {
      await api.post('/api/onboarding/step2', body);
      currentStep = 3;
      renderStep(3);
    } catch (err) {
      errEl.textContent = err.message;
      errEl.classList.add('show');
    }
  });
}

// ── STEP 3 — What Are You Building Toward? ────────────────────────────────

function renderStep3(el) {
  const d = stepData.step3 ?? {};

  el.innerHTML = `
    ${backBtn()}
    <div class="step-title">What are you building toward?</div>
    <div class="step-subtitle">This shapes how we frame your progress.</div>

    <div class="form-group">
      <label class="form-label">My financial goal is</label>
      <input type="number" id="goal-amount" value="${d.goalAmount ?? 1000000}" step="10000"
        style="font-family:var(--font-mono);font-size:1.1rem;font-weight:700">
      <div class="field-helper">This is your long-term target. $1M is the default but it's yours to set.</div>
    </div>

    <div class="form-group">
      <label class="form-label">Expected annual investment return</label>
      <div style="display:flex;align-items:center;gap:var(--space-3)">
        <input type="range" id="return-slider" min="3" max="15" step="0.5"
          value="${d.returnRatePct ?? 7}"
          style="flex:1;accent-color:var(--accent);cursor:pointer">
        <div style="font-family:var(--font-mono);font-weight:700;width:40px;text-align:right" id="return-label">${d.returnRatePct ?? 7}%</div>
      </div>
      <div class="field-helper">7% is the S&P 500 historical average. Use 5% to be conservative, 10% to be optimistic.</div>
    </div>

    <div class="form-group">
      <label class="form-label">I plan to invest approximately</label>
      <div style="display:flex;align-items:center;gap:var(--space-2)">
        <span style="color:var(--text-secondary)">$</span>
        <input type="number" id="monthly-invest" value="${d.monthlyInvestAmt ?? 300}" step="50"
          style="font-family:var(--font-mono)">
        <span style="color:var(--text-secondary);white-space:nowrap">/&nbsp;month</span>
      </div>
      <div class="field-helper">What you aim to put into savings or your Roth IRA. Adjust anytime.</div>
    </div>

    <div style="background:var(--bg-secondary);border-radius:var(--radius-md);padding:var(--space-4);margin-top:var(--space-2);margin-bottom:var(--space-5)">
      <div style="font-size:.7rem;color:var(--text-tertiary);text-transform:uppercase;letter-spacing:.06em;font-weight:600;margin-bottom:var(--space-3)">These are assumptions, not commitments.</div>
      <div style="font-size:.8rem;color:var(--text-secondary);line-height:1.5">The app updates your timeline as your actual data comes in.</div>
    </div>

    <div id="step3-err" class="auth-error"></div>
    <button class="btn btn-primary w-full" id="step3-continue" style="justify-content:center;padding:14px">Continue →</button>`;

  wireBack(3);

  el.querySelector('#return-slider').addEventListener('input', (e) => {
    el.querySelector('#return-label').textContent = `${e.target.value}%`;
  });

  el.querySelector('#step3-continue').addEventListener('click', async () => {
    const errEl = el.querySelector('#step3-err');
    errEl.classList.remove('show');
    const goalAmount       = parseFloat(el.querySelector('#goal-amount').value) || 1_000_000;
    const returnRatePct    = parseFloat(el.querySelector('#return-slider').value) || 7;
    const monthlyInvestAmt = parseFloat(el.querySelector('#monthly-invest').value) || 300;

    stepData.step3 = { goalAmount, returnRatePct, monthlyInvestAmt };
    try {
      await api.post('/api/onboarding/step3', { goalAmount, returnRatePct, monthlyInvestAmt });
      currentStep = 4;
      // Load categories before step 4
      try { categories = await api.get('/api/categories').catch(() => []); } catch {}
      renderStep(4);
    } catch (err) {
      errEl.textContent = err.message;
      errEl.classList.add('show');
    }
  });
}

// ── STEP 4 — Time Allocation (skippable) ─────────────────────────────────

const CAT_DEFAULTS = { work: 40, school: 15, growth: 5, fun: 5, relationships: 3, health: 3 };

function renderStep4(el) {
  const allCats = categories.length ? categories : [
    { id: 'work',          name: 'Work',          color: CATEGORY_COLORS.work },
    { id: 'school',        name: 'School',        color: CATEGORY_COLORS.school },
    { id: 'growth',        name: 'Growth',        color: CATEGORY_COLORS.growth },
    { id: 'fun',           name: 'Fun',           color: CATEGORY_COLORS.fun },
    { id: 'relationships', name: 'Relationships', color: CATEGORY_COLORS.relationships },
    { id: 'health',        name: 'Health',        color: CATEGORY_COLORS.health },
  ];

  el.innerHTML = `
    ${backBtn()}
    <div style="display:flex;justify-content:space-between;align-items:flex-start">
      <div>
        <div class="step-title">How do you want to spend your time?</div>
        <div class="step-subtitle">Weekly targets for each area of life — not rules, just intentions.</div>
      </div>
    </div>

    <button class="btn btn-ghost" id="step4-skip-top"
      style="font-size:.75rem;color:var(--text-tertiary);margin-bottom:var(--space-4)">Skip — I'll set this up later</button>

    <div id="alloc-rows">
      ${allCats.map(cat => {
        const key = cat.name.toLowerCase();
        const def = CAT_DEFAULTS[key] ?? 3;
        return `
          <div class="alloc-row">
            <div class="alloc-dot" style="background:${esc(cat.color)}"></div>
            <div class="alloc-name">${esc(cat.name)}</div>
            <input type="number" class="alloc-input" data-cat-id="${esc(cat.id)}"
              placeholder="0" min="0" max="168" value="${def}">
            <span style="font-size:.8rem;color:var(--text-secondary)">h/wk</span>
          </div>`;
      }).join('')}
    </div>

    <div class="alloc-total">
      <div class="alloc-total-row">
        <span style="color:var(--text-secondary)">Total allocated</span>
        <span class="alloc-total-num" id="alloc-total-num">—</span>
      </div>
      <div class="progress-bar">
        <div class="progress-fill" id="alloc-bar" style="width:0%;background:var(--accent)"></div>
      </div>
      <div style="font-size:.7rem;color:var(--text-tertiary);margin-top:var(--space-2)">
        168 hours in a week · Sleep ~56h · ~112h available
      </div>
    </div>

    <div id="step4-err" class="auth-error" style="margin-top:var(--space-4)"></div>
    <button class="btn btn-primary w-full" id="step4-continue"
      style="justify-content:center;padding:14px;margin-top:var(--space-5)">Continue →</button>`;

  wireBack(4);

  // Live total bar
  function updateTotal() {
    const total = Array.from(el.querySelectorAll('.alloc-input'))
      .reduce((s, i) => s + (parseFloat(i.value) || 0), 0);
    el.querySelector('#alloc-total-num').textContent = `${total.toFixed(0)}h / 168h`;
    const pct = Math.min(100, (total / 112) * 100); // 112h = ~awake time
    el.querySelector('#alloc-bar').style.width = `${pct}%`;
    el.querySelector('#alloc-bar').style.background = pct > 100 ? 'var(--negative)' : 'var(--accent)';
  }
  el.querySelectorAll('.alloc-input').forEach(i => i.addEventListener('input', updateTotal));
  updateTotal();

  const skip = async () => {
    await api.post('/api/onboarding/step4', { skipped: true });
    currentStep = 5;
    renderStep(5);
  };

  el.querySelector('#step4-skip-top').addEventListener('click', skip);

  el.querySelector('#step4-continue').addEventListener('click', async () => {
    const errEl = el.querySelector('#step4-err');
    errEl.classList.remove('show');
    const allocations = Array.from(el.querySelectorAll('.alloc-input'))
      .map(i => ({ categoryId: i.dataset.catId, weeklyHours: parseFloat(i.value) || 0 }))
      .filter(a => a.weeklyHours > 0);

    try {
      await api.post('/api/onboarding/step4', { allocations, skipped: false });
      currentStep = 5;
      renderStep(5);
    } catch (err) {
      errEl.textContent = err.message;
      errEl.classList.add('show');
    }
  });
}

// ── STEP 5 — You're Ready ─────────────────────────────────────────────────

function renderStep5(el) {
  el.innerHTML = `
    ${backBtn()}
    <div class="step-title">You're ready.</div>
    <div class="step-subtitle">Your dashboard is set up. One last thing:</div>

    <div class="data-choice-grid">
      <div class="data-choice-card selected" data-choice="real">
        <div class="data-choice-radio"></div>
        <div class="data-choice-body">
          <div class="choice-title">Start with my real data</div>
          <div class="choice-desc">Your dashboard shows your actual numbers. A few sections will be empty until you log your first paycheck and expenses.</div>
        </div>
      </div>
      <div class="data-choice-card" data-choice="sample">
        <div class="data-choice-radio"></div>
        <div class="data-choice-body">
          <div class="choice-title">Show me a full example first</div>
          <div class="choice-desc">Load sample data so you can explore the app before adding your own numbers. Sample data is clearly labeled and removes itself with one tap.</div>
        </div>
      </div>
    </div>

    <div id="step5-err" class="auth-error"></div>
    <button class="btn btn-primary w-full" id="step5-go"
      style="justify-content:center;padding:14px;font-size:1rem">Go to my dashboard →</button>`;

  wireBack(5);

  el.querySelectorAll('.data-choice-card').forEach(card => {
    card.addEventListener('click', () => {
      el.querySelectorAll('.data-choice-card').forEach(c => c.classList.remove('selected'));
      card.classList.add('selected');
    });
  });

  el.querySelector('#step5-go').addEventListener('click', async () => {
    const errEl = el.querySelector('#step5-err');
    errEl.classList.remove('show');
    const choice = el.querySelector('.data-choice-card.selected')?.dataset.choice ?? 'real';
    try {
      await api.post('/api/onboarding/complete', { useSampleData: choice === 'sample' });
      // Transition to app
      document.getElementById('onboarding-screen').classList.add('hidden');
      // Signal app.js to show the dashboard
      window.dispatchEvent(new CustomEvent('onboarding-complete'));
    } catch (err) {
      errEl.textContent = err.message;
      errEl.classList.add('show');
    }
  });
}
